import { endOfDay, startOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

// fromZonedTime so the ISO instant is the zone's day boundary, not the browser's
export const boundaryToIso = (date, timeZone) =>
  (timeZone ? fromZonedTime(date, timeZone) : date).toISOString();

// A day the user picked on the calendar is a calendar day, not an instant. Pickers hand back
// browser-local Dates, so re-anchor the day boundaries to the practice/user zone — otherwise a
// biller in Pacific filtering an Eastern practice shifts the window by the offset between them.
export const toZonedDayStart = (date, timeZone) =>
  date ? boundaryToIso(startOfDay(new Date(date)), timeZone) : null;

export const toZonedDayEnd = (date, timeZone) =>
  date ? boundaryToIso(endOfDay(new Date(date)), timeZone) : null;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const US_DATE_ONLY_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const pad = (n) => String(n).padStart(2, '0');

export const isCalendarDay = (value) => typeof value === 'string' && DATE_ONLY_RE.test(value);

// Normalize anything the picker or a preset hands back to a single calendar-day form, 'YYYY-MM-DD' —
// the shape a Postgres DATE column stores and compares against, with no instant and no zone involved.
// An instant is read in `timeZone` first, so "today" is the day the user is actually having.
export const toCalendarDay = (value, timeZone) => {
  if (!value) return null;
  if (isCalendarDay(value)) return value;

  const us = typeof value === 'string' && US_DATE_ONLY_RE.exec(value);
  if (us) return `${us[3]}-${pad(us[1])}-${pad(us[2])}`;

  const d = timeZone ? toZonedTime(new Date(value), timeZone) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// A range this picker produced carries the zone it was anchored in and is returned untouched. One
// built anywhere else — a feature's default filter, an older saved view — is a bare date ('06/02/2026')
// or a browser-local Date: no time and no zone, so reading it as an instant lands on the previous day
// at 9 PM. Those get anchored to whole days in `timeZone`.
//
// Applied at render/request time rather than stored, because the zone arrives asynchronously from user
// preferences — anchoring into state would freeze whatever zone happened to be resolved first.
// Force a range to calendar days. Used for date-only filters, where the stored value may still be an
// instant — a range picked before the filter was marked date-only, or an older saved view. Coercing on
// read means such a filter renders and re-emits as days without needing the stored data migrated.
export const toCalendarDayRange = (values, timeZone) => {
  if (!values?.startDate || !values?.endDate) return values;

  const { timezone: _dropped, ...rest } = values;
  return {
    ...rest,
    startDate: toCalendarDay(values.startDate, timeZone),
    endDate: toCalendarDay(values.endDate, timeZone)
  };
};

export const anchorRangeToZone = (values, timeZone) => {
  if (!values?.startDate || !values?.endDate) return values;
  if (values.timezone) return values;

  // A date-only filter targets a DATE column: its boundaries are already calendar days and turning
  // them into instants is exactly what this whole path exists to avoid.
  if (isCalendarDay(values.startDate) && isCalendarDay(values.endDate)) return values;

  return {
    ...values,
    timezone: timeZone,
    startDate: toZonedDayStart(values.startDate, timeZone),
    endDate: toZonedDayEnd(values.endDate, timeZone)
  };
};
